import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from 'react';
import Pusher from 'pusher-js';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { slug } from '@utils/api/helpers';

interface PusherContextValue {
  pusher: Pusher | null;
  isConnected: boolean;
}

const PusherContext = createContext<PusherContextValue | null>(null);

interface PusherProviderProps {
  children: ReactNode;
}

export const PusherProvider: React.FC<PusherProviderProps> = ({ children }) => {
  const [pusher, setPusher] = useState<Pusher | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const wasConnectedRef = useRef(false);

  useEffect(() => {
    const pusherKey = import.meta.env.VITE_PUSHER_KEY;
    const pusherCluster = import.meta.env.VITE_PUSHER_CLUSTER;

    if (!pusherKey || !pusherCluster) {
      const error = new Error('Pusher configuration missing');
      if (import.meta.env.DEV) {
        console.error(
          '[Pusher] Missing VITE_PUSHER_KEY or VITE_PUSHER_CLUSTER env vars'
        );
      } else {
        Sentry.captureException(error);
      }
      return;
    }

    const authEndpoint = `/nemo${slug}/patients/websocket`;
    if (import.meta.env.DEV) {
      console.log('[Pusher] Connecting with:', { authEndpoint, pusherKey, pusherCluster });
    }

    const pusherInstance = new Pusher(pusherKey, {
      cluster: pusherCluster,
      channelAuthorization: {
        endpoint: authEndpoint,
        transport: 'ajax',
      },
    });

    // Connection event handlers
    pusherInstance.connection.bind('connected', () => {
      setIsConnected(true);
      wasConnectedRef.current = true;
      toast.dismiss('pusher-disconnected');
    });

    pusherInstance.connection.bind('disconnected', () => {
      setIsConnected(false);
      if (wasConnectedRef.current) {
        toast.error('Real-time connection lost. Updates may be delayed.', {
          id: 'pusher-disconnected',
          duration: Infinity,
        });
      }
    });

    pusherInstance.connection.bind('error', (err: unknown) => {
      // Transient WebSocket errors are expected on mobile/spotty networks
      // Pusher auto-reconnects with exponential backoff, so we only log in dev
      if (import.meta.env.DEV) {
        console.error('[Pusher] Connection error:', err);
      }
    });

    setPusher(pusherInstance);

    return () => {
      toast.dismiss('pusher-disconnected');
      pusherInstance.disconnect();
      setPusher(null);
      setIsConnected(false);
    };
  }, []);

  return (
    <PusherContext.Provider value={{ pusher, isConnected }}>
      {children}
    </PusherContext.Provider>
  );
};

export const usePusher = () => {
  const context = useContext(PusherContext);
  if (!context) {
    throw new Error('usePusher must be used within a PusherProvider');
  }
  return context;
};
